/** Who dives, and when (docs/spec/02 §5). */

import { launchMonarch, launchMoth, launchStinger, startMorph } from './dive';
import { groupsInFlight, isAloft, isFrenzy } from './roster';
import type { Difficulty } from './tuning';
import { DIVE } from './tuning';
import type { Director, Enemy, GameState } from './types';

export function newDirector(): Director {
  return { timer: 0, turn: 0, nextGroup: 1, nextTrio: 1, brokenTrios: [] };
}

/** A beam run needs a lone fighter in the air, and no captive or beam anywhere (02 §6). */
function canBeam(s: GameState): boolean {
  const p = s.player;
  if (p.status !== 'flying' || p.dual || s.beam || s.rescue) return false;
  return !s.enemies.some((e) => e.kind === 'captive' || (e.after === 'beam' && isAloft(e)));
}

function morphing(s: GameState): boolean {
  return s.enemies.some((e) => e.state === 'morphing' || e.kind === 'morph');
}

/** Send `e` on its way. `special`: it may turn out a beam run or a morph. */
function launch(s: GameState, e: Enemy, d: Difficulty, special: boolean): void {
  switch (e.kind) {
    case 'monarch':
      launchMonarch(s, e, d, special && canBeam(s) && s.rng.oneIn(DIVE.beamOneIn));
      return;
    case 'moth':
    case 'captive':
      launchMoth(s, e, d);
      return;
    default:
      if (special && s.stage >= DIVE.morphFrom && !morphing(s) && s.rng.oneIn(DIVE.morphOneIn)) startMorph(s, e);
      else launchStinger(s, e, d);
  }
}

/** Whose turn it is: 0 Stingers, 1 Moths (and rogue captives), 2 Monarchs. */
function takesTurn(e: Enemy, turn: number): boolean {
  if (e.state !== 'formed') return false;
  if (turn === 0) return e.kind === 'stinger';
  if (turn === 1) return e.kind === 'moth' || (e.kind === 'captive' && e.partner < 0);
  return e.kind === 'monarch';
}

/** Launch the next group, taking the kinds in turn and skipping any with nobody home. */
function launchNext(s: GameState, d: Difficulty): boolean {
  const dir = s.director;
  for (let k = 0; k < 3; k++) {
    const turn = (dir.turn + k) % 3;
    const home = s.enemies.filter((e) => takesTurn(e, turn));
    const e = home[s.rng.int(home.length)];
    if (!e) continue;
    dir.turn = (turn + 1) % 3;
    launch(s, e, d, true);
    return true;
  }
  return false;
}

export function updateDirector(s: GameState, d: Difficulty): void {
  if (s.phase !== 'battle') return;
  // Nothing new starts while the fighter is down, arriving, in a beam or being handed back.
  if (s.player.status !== 'flying' || s.rescue) return;
  const dir = s.director;
  if (isFrenzy(s)) {
    for (const e of s.enemies) {
      const rogue = e.kind === 'captive' && e.partner < 0;
      if (e.state === 'formed' && e.timer >= 8 && (e.kind !== 'captive' || rogue)) launch(s, e, d, false);
    }
    return;
  }
  if (--dir.timer > 0) return;
  if (groupsInFlight(s) >= d.maxGroups || !launchNext(s, d)) {
    dir.timer = 6;
    return;
  }
  dir.timer = d.launchEvery - 8 + s.rng.int(17);
}
